import { DebugLogger } from '@affine/debug';
import { Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';

import { AccessibleDocCacheStore } from '../doc/stores/accessible-doc-cache';
import { WorkspaceAreaCacheStore } from '../folder-api/stores/workspace-area-cache';
import { AccessibleFolderCacheStore } from '../organize/stores/accessible-folder-cache';
import { TagStore } from '../tag/stores/tag';
import type { WorkspaceMetadata } from '../workspace/metadata';
import { WorkspaceService } from '../workspace/services/workspace';
import { WorkspacesService } from '../workspace/services/workspaces';
import { recordWorkspaceProjectionDiagnostic } from './diagnostics';
import type {
  WorkspaceMetadataChange,
  WorkspacePermissionChange,
} from './types';

const logger = new DebugLogger('workspace-projection-reconciler');

/**
 * How long server events wait to be applied together. An import creates
 * hundreds of documents and the server reports each one; applied together,
 * the stores change in one task and sidebar and lists render once per burst.
 */
export const PROJECTION_EVENT_COALESCE_MS = 50;

type QueuedProjectionEvent =
  | { kind: 'permission'; event: WorkspacePermissionChange }
  | { kind: 'metadata'; event: WorkspaceMetadataChange };

/**
 * One workspace-scoped owner for server projection invalidations.
 *
 * UI routes and individual stores must not subscribe to workspace mutation
 * streams independently. This service turns each server event into one typed
 * reconciliation pass; sidebar, /all and /trash then converge through the same
 * document/folder/area stores.
 */
export class WorkspaceProjectionReconciler extends Service {
  private permissionSubscription: Subscription | null = null;
  private metadataSubscription: Subscription | null = null;
  /** Server events of the current burst, in arrival order across both streams. */
  private queuedEvents: QueuedProjectionEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspacesService: WorkspacesService,
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore,
    private readonly workspaceAreaCache: WorkspaceAreaCacheStore,
    private readonly tagStore: TagStore
  ) {
    super();
  }

  prime(): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') return;

    if (!this.permissionSubscription) {
      this.permissionSubscription =
        workspace.engine.permissionsChanged$?.subscribe(event => {
          this.enqueue({ kind: 'permission', event });
        }) ?? null;
    }
    if (!this.metadataSubscription) {
      this.metadataSubscription =
        workspace.engine.workspaceMetaChanged$?.subscribe(event => {
          this.enqueue({ kind: 'metadata', event });
        }) ?? null;
    }
  }

  private enqueue(queued: QueuedProjectionEvent): void {
    this.queuedEvents.push(queued);
    // Losing the workspace is applied at once, together with what came before.
    if (
      queued.kind === 'permission' &&
      queued.event.changeType === 'workspace_member_revoked'
    ) {
      this.flushQueuedEvents();
      return;
    }
    this.flushTimer ??= setTimeout(
      () => this.flushQueuedEvents(),
      PROJECTION_EVENT_COALESCE_MS
    );
  }

  private flushQueuedEvents(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    const events = this.queuedEvents;
    this.queuedEvents = [];
    for (const queued of events) {
      if (queued.kind === 'permission') {
        this.reconcilePermissionChange(queued.event);
      } else {
        this.reconcileMetadataChange(queued.event);
      }
    }
  }

  reconcilePermissionChange(event: WorkspacePermissionChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;

    recordWorkspaceProjectionDiagnostic({
      stage: 'reconciler:permission-received',
      changeType: event.changeType,
      docIds: event.affectedDocIds,
      folderIds: [
        ...(event.affectedFolderIds ?? []),
        ...(event.revokedFolderIds ?? []),
      ],
    });

    if (event.changeType === 'workspace_member_revoked') {
      void this.handleWorkspaceRevocation(workspace.meta);
      return;
    }

    if (event.revokedFolderIds?.length) {
      const revokedDocIds =
        this.accessibleFolderCache.removeRevokedFolderSubtreesLocally(
          event.revokedFolderIds
        );
      this.accessibleDocCache.removeRevokedDocsLocally(revokedDocIds);
    }

    this.accessibleDocCache.reconcilePermissionChange(event);
    const reconciledFolderDocs =
      this.accessibleFolderCache.reconcilePermissionChange(event);
    this.workspaceAreaCache.reconcilePermissionChange(event);
    if (reconciledFolderDocs) {
      void reconciledFolderDocs
        .then(({ grantedDocIds, revokedDocIds }) => {
          if (revokedDocIds.length > 0) {
            this.accessibleDocCache.removeRevokedDocsLocally(revokedDocIds);
          }
          const eventDocIds = new Set(event.affectedDocIds ?? []);
          const docsToHydrate = grantedDocIds.filter(
            docId => !eventDocIds.has(docId)
          );
          if (docsToHydrate.length === 0) {
            return;
          }
          return this.accessibleDocCache.fetchDocsByIds(
            docsToHydrate,
            undefined,
            {
              diagnosticReason: `permission-event:${event.changeType}`,
              permissionGraphVersion: event.permissionGraphVersion,
              removeMissing: true,
            }
          );
        })
        .catch(() => {
          // Reconnect/visibility reconciliation retries transient failures.
        });
    }

    if (
      event.changeType === 'workspace_member_joined' ||
      event.changeType === 'workspace_member_role_changed' ||
      event.changeType === 'workspace_member_left'
    ) {
      this.workspacesService.list.revalidate();
      void this.workspaceAreaCache.refresh().catch(() => {});
    }
  }

  reconcileMetadataChange(event: WorkspaceMetadataChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;

    recordWorkspaceProjectionDiagnostic({
      stage: 'reconciler:metadata-received',
      changeType: event.changeType,
      docIds: event.docId ? [event.docId] : undefined,
    });

    this.accessibleDocCache.reconcileMetadataChange(event);
    this.accessibleFolderCache.reconcileMetadataChange(event);
    this.workspaceAreaCache.reconcileMetadataChange(event);
    this.tagStore.reconcileMetadataChange(event);

    if (event.changeType === 'workspace_deleted') {
      this.workspacesService.list.revalidate();
    }
  }

  private async handleWorkspaceRevocation(
    meta: WorkspaceMetadata
  ): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      await this.workspacesService.eraseLocalData(meta);
    } catch (error) {
      logger.error('Local erase of revoked workspace failed', error);
      recordWorkspaceProjectionDiagnostic({
        stage: 'reconciler:local-erase-failed',
        reason: error instanceof Error ? error.message : String(error),
      });
    } finally {
      window.location.href = '/';
    }
  }

  override dispose(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.queuedEvents = [];
    this.permissionSubscription?.unsubscribe();
    this.permissionSubscription = null;
    this.metadataSubscription?.unsubscribe();
    this.metadataSubscription = null;
    super.dispose();
  }
}
